import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { HrBotContext } from 'components/@home/drawers/HrBotDrawer/hrBotContext';
import extractionFileMatchesForSend from 'components/@home/@documents/SentDocuments/extractionFileMatch';
import Pagination from 'components/controls/Pagination';
import capitalize from 'lodash/capitalize';
import get from 'lodash/get';
import FindInPageIcon from 'mdi-react/FindInPageIcon';
import { array } from 'prop-types';
import React, { memo, useCallback, useContext, useState } from 'react';
import useDimensions from 'react-cool-dimensions';
import { I18n } from 'utils/i18n';

import Search from './Search';
import searchFiles from './searchFiles';
import TitleCell from './TitleCell';

const PER_PAGE = 20;

const DroppedFiles = ({ files }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRows] = useState(PER_PAGE);
  const [searchValue, setSearchValue] = useState('');
  const { navigateTo, setCurrentFile, format } = useContext(HrBotContext);
  const { observe } = useDimensions({
    onResize: ({ height }) => {
      setPage(0);
      setRows(Math.floor((height - 50) / 40));
    },
  });

  const [order, setOrder] = useState({ by: 'employee.name', isReverse: false });
  const error = files.some(f => f.error);
  const hasDni = files.some(f => f.dni);

  const handleSearch = event => {
    setSearchValue(event.currentTarget.value);
    setPage(0);
  };

  const changePage = useCallback(({ selected }) => setPage(selected), []);

  const newFiles = files.map((f, idx) => ({
    ...f,
    [format.sendBy]: f[format.sendBy] && f[format.sendBy].padStart(5, '0'),
    idx,
  }));

  const filtered = searchFiles(searchValue, newFiles).filter(value => !error || value.error);
  const sortFunction = (first, second) => {
    const f = get(first, order.by);
    const s = get(second, order.by);
    let ret = 0;
    if (typeof f === typeof s && typeof f === 'string') {
      ret = f.localeCompare(s);
    } else if (typeof f === typeof s && typeof f === 'undefined') {
      ret = 0;
    } else if (typeof f === 'undefined') {
      ret = -1;
    } else if (typeof s === 'undefined') {
      ret = 1;
    }
    return order.isReverse ? -ret : ret;
  };

  const sorted = filtered.sort(sortFunction);

  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const sendByField = format.sendBy || 'code';
  const fatalError = files.some(f => f.error);
  const numSend = files.filter(f => extractionFileMatchesForSend(f, sendByField)).length;
  const numErrors = files.filter(f => f.error).length;
  const numNotFound = files.filter(
    f => !f.error && !extractionFileMatchesForSend(f, sendByField),
  ).length;

  return (
    <Box
      sx={{
        display: 'flex',
        flexGrow: 1,
        flexDirection: 'column',
        maxHeight: '100%',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <Box sx={{ display: 'flex' }}>
          {!fatalError && (
            <Typography
              sx={{
                display: 'inline',
              }}
            >
              {I18n.t('HrBot.recipients', { count: numSend })}
            </Typography>
          )}
          {!fatalError && numNotFound > 0 && (
            <Typography
              color="error"
              sx={{
                display: 'inline',
                ml: 1,
              }}
            >
              {I18n.t('HrBot.notFound', { count: numNotFound })}
            </Typography>
          )}
          {numErrors > 0 && (
            <Typography
              color="error"
              sx={{
                display: 'inline',
                ml: 1,
              }}
            >
              {I18n.t('HrBot.errors', { count: numErrors })}
            </Typography>
          )}
        </Box>
        <Search onChange={handleSearch} value={searchValue} />
      </Box>
      <Box sx={{ flexGrow: 1, maxHeight: '100%', overflow: 'hidden' }} ref={observe}>
        <Table sx={{ maxHeight: '100%', overflow: 'hidden' }} aria-labelledby="employeesTable">
          <TableHead
            sx={theme => ({
              width: '100%',
              borderTop: `1px solid ${theme.palette.divider}`,
            })}
          >
            <TableRow sx={{ height: 40 }}>
              <TitleCell
                name="employee.name"
                label={I18n.t('HrBot.Name')}
                order={order}
                setOrder={setOrder}
                padding="none"
              />
              <TitleCell
                name={format.sendBy}
                label={I18n.t(`HrBot.${capitalize(format.sendBy)}`)}
                order={order}
                setOrder={setOrder}
              />
              <TitleCell
                name="file.name"
                label={I18n.t('HrBot.Original file name')}
                order={order}
                setOrder={setOrder}
              />
              {!error && (
                <TitleCell
                  name="newFileName"
                  label={I18n.t('HrBot.New file name')}
                  order={order}
                  setOrder={setOrder}
                />
              )}
              {hasDni && (
                <TitleCell
                  name="dni"
                  label={I18n.t('HrBot.Password')}
                  order={order}
                  setOrder={setOrder}
                />
              )}
              {error && <TableCell>{I18n.t('HrBot.Errors')}</TableCell>}
              <TableCell align="right">{I18n.t('HrBot.Find elements')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.map(value => {
              const notFound = (
                <Box component="span" sx={theme => ({ color: theme.palette.danger })}>
                  {I18n.t('HrBot.User not registered at Ommnio')}
                </Box>
              );
              const openConfig = () => {
                setCurrentFile(value);
                setTimeout(() => navigateTo('config'), 200);
              };
              let val = value[format.sendBy];
              if (!val && value.routedByDni && value.dni) {
                val = value.dni;
              }
              if (val && format.items.find(i => i.name === 'division')) {
                val = (
                  <>
                    {val}
                    <br />
                    {` (${value.division})`}
                  </>
                );
              }
              return (
                <TableRow sx={{ height: 40 }} key={value.idx}>
                  <TableCell padding="none">
                    {fatalError ? I18n.t('HrBot.not found') : value.employee.name || notFound}
                  </TableCell>
                  <TableCell>{val || I18n.t('HrBot.not found')}</TableCell>
                  <TableCell>
                    {format.type === 'xml'
                      ? value.filename || (
                          <Box component="span" sx={theme => ({ color: theme.palette.danger })}>
                            {I18n.t('HrBot.not found')}
                          </Box>
                        )
                      : value.file.name}
                    {format.type === 'single-pdf' && ` p.${value.pageNum}`}
                  </TableCell>
                  {!error && <TableCell>{value.newFileName}</TableCell>}
                  {hasDni && <TableCell>{value.dni}</TableCell>}
                  {error && (
                    <TableCell>
                      <Box component="span" sx={theme => ({ color: theme.palette.danger })}>
                        {Object.entries(value)
                          .filter(([k, v]) => !v && k !== 'idx' && k !== 'newFileName')
                          .map(([k]) => I18n.t(`HrBot.fields.${k}`))
                          .join(', ')}
                      </Box>
                    </TableCell>
                  )}
                  <TableCell align="right">
                    <IconButton onClick={openConfig} sx={{ p: '4px' }} size="large">
                      <FindInPageIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
      <div>
        <Pagination
          perPage={rowsPerPage}
          total={filtered.length}
          page={page}
          onChange={changePage}
        />
      </div>
    </Box>
  );
};

DroppedFiles.propTypes = {
  files: array.isRequired,
};

export default memo(DroppedFiles);
